import { getLocalizedField, type PricingPlan } from "../api";
import type { Locale } from "@/lib/i18n";

export function isMostPopularHeuristic(plan: PricingPlan): boolean {
  const en = (plan.badge_en ?? "").toLowerCase();
  if (en.includes("popular") || en.includes("most popular")) return true;
  if (plan.badge_bn?.includes("জনপ্রিয়")) return true;
  return false;
}

export function hasFutureSprint(endsAt: string | undefined): boolean {
  if (!endsAt) return false;
  const time = new Date(endsAt).getTime();
  return Number.isFinite(time) && time > Date.now();
}

export function getSprintPlanIdForMulti(plans: PricingPlan[]): string | null {
  const sorted = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    sorted.find(
      (plan) => isMostPopularHeuristic(plan) && hasFutureSprint(plan.sprintEndsAt),
    )?._id ??
    sorted.find((plan) => hasFutureSprint(plan.sprintEndsAt))?._id ??
    null
  );
}

/**
 * `courseDetail.seats.*`, translated by the caller. The caller spells the keys
 * out: a translator handed to a helper ships its whole namespace to the browser
 * (`scripts/i18n-client-namespaces.mjs`).
 */
export type SeatIndicatorLabels = {
  full: string;
  limited: string;
  onlyLeft: (count: number) => string;
};

export function getSeatIndicator(
  plan: PricingPlan,
  labels: SeatIndicatorLabels,
): { label: string; warm: boolean; full: boolean } | null {
  const status = plan.seatStatus;
  if (!status || status.total == null || status.remaining == null) return null;
  const { total, remaining } = status;
  if (total <= 0) return null;
  if (remaining <= 0) return { label: labels.full, warm: true, full: true };

  const ratio = remaining / total;
  if (ratio > 0.5) return null;
  if (ratio <= 0.25) {
    return {
      label: labels.onlyLeft(remaining),
      warm: ratio <= 0.1,
      full: false,
    };
  }
  return { label: labels.limited, warm: false, full: false };
}

export function normalizePricingFeatureKey(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Union of feature rows across plans (order: first seen in sortOrder, then feature order).
 */
export function buildPricingFeatureRowKeys(
  plans: PricingPlan[],
  locale: Locale,
): { key: string; label: string }[] {
  const sorted = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);
  const order: string[] = [];
  const labelByKey = new Map<string, string>();
  for (const plan of sorted) {
    for (const f of plan.features) {
      const label =
        getLocalizedField(f.text_en, f.text_bn, locale) ?? f.text_en;
      const key = normalizePricingFeatureKey(label);
      if (!key) continue;
      if (!labelByKey.has(key)) {
        labelByKey.set(key, label.trim());
        order.push(key);
      }
    }
  }
  return order.map((k) => ({ key: k, label: labelByKey.get(k) ?? k }));
}

export function planHasFeatureKey(
  plan: PricingPlan,
  key: string,
  locale: Locale,
): boolean {
  return plan.features.some((f) => {
    const label = getLocalizedField(f.text_en, f.text_bn, locale) ?? f.text_en;
    return normalizePricingFeatureKey(label) === key;
  });
}
